import { AfterViewInit, Component, Input, NgZone, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import * as Highcharts from 'highcharts';
import { CHART_COLORS, CHART_FONTS, CHART_STYLES, HighchartsOptions }
  from 'src/app/domain/shared/component/pie-chart-simple/highcharts-common/highcharts-global-const';

/**
 * This is BASE PIE CHART
 *
 * ▒█▀▀█ ▒█░▒█ ░█▀▀█ ▒█▄░▒█ ▒█▀▀█ ▒█▀▀▀ ▒█▀▀▀█ 　 ▒█▄░▒█ ▒█▀▀▀█ ▀▀█▀▀ 　 ░█▀▀█ ▒█░░░ ▒█░░░ ▒█▀▀▀█ ▒█░░▒█ ▒█▀▀▀ ▒█▀▀▄
 * ▒█░░░ ▒█▀▀█ ▒█▄▄█ ▒█▒█▒█ ▒█░▄▄ ▒█▀▀▀ ░▀▀▀▄▄ 　 ▒█▒█▒█ ▒█░░▒█ ░▒█░░ 　 ▒█▄▄█ ▒█░░░ ▒█░░░ ▒█░░▒█ ▒█▒█▒█ ▒█▀▀▀ ▒█░▒█
 * ▒█▄▄█ ▒█░▒█ ▒█░▒█ ▒█░░▀█ ▒█▄▄█ ▒█▄▄▄ ▒█▄▄▄█ 　 ▒█░░▀█ ▒█▄▄▄█ ░▒█░░ 　 ▒█░▒█ ▒█▄▄█ ▒█▄▄█ ▒█▄▄▄█ ▒█▄▀▄█ ▒█▄▄▄ ▒█▄▄▀
 * to make changes here, ask Ivan or Dmytro
 *
 * How to use:
 *  Very simple, just add this in your component
 *
 *  <app-base-pie-chart
 *    *ngIf="chart.showCharts"                            // Is needed only when you want to set another data
 *    [chartId]="'lol-kek-cheburek'"                      // Chart id, needed for html rendering
 *    [additionalOptions]="data.data.veryImportant.data"> // Object, which contains additional options: HighchartsOptions.
 *   </app-base-pie-chart>
 *
 * How to fill additional options:
 *     Create separate .ts service for chart options calculations
 *
 *     EXAMPLE -> also check real example FairValueAndPpChartsOptionsService
 *
 *     private static createChartOptions(chartData: PortfolioPerformance): FairValueChartInformation {
 *          const preparedSimpleArray1 = collectUsingMagicThisData(chartData);
 *          const preparedSimpleArray2 = collectUsingMagicThisData(chartData);
 *          const preparedSimple_X_AXIS_Array = collectUsingMagicThisData(chartData);
 *
 *          return {
 *            chartId,
 *            additionalOptions: {
 *
 *              tooltip: CHART_STYLES.yourTooltipIfNeeded,
 *              series: [{
 *                name: 'Nice name1 for nice data',
 *                data: preparedSimpleArray1,
 *              },
 *              {
 *                name: 'Nice name2 for nice data',
 *                data: preparedSimpleArray2,
 *              }
 *              ],
 * IMPORTANT: to add the WHOLE xAxis, to add only categories is not enough, in order to save styles
 *              xAxis: [{
 *                   categories: preparedSimple_X_AXIS_Array,
 *                   type: 'category',
 *                   crosshair: true,
 *                   style: { color: '#333333', fontSize: CHART_FONTS.font4 },
 *                   labels: {
 *                     rotation: -45, // Rotate labels
 *                     style: {
 *                       width: 75,
 *                       textOverflow: 'ellipsis',
 *                       fontSize: CHART_FONTS.font4,
 *                     },
 *                   },
 *                   scrollbar: CHART_STYLES.scrollbar,
 *                 }],
 *            },
 *          };
 *        }
 *
 */

@Component({
  selector: 'app-base-pie-chart',
  templateUrl: './base-pie-chart.component.html',
  styleUrls: ['./base-pie-chart.component.scss'],
})
export class BasePieChartComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() additionalOptions: Partial<HighchartsOptions> = {};
  @Input() chartId: string;
  @Input() height = 'height-28'; // default height

  options: HighchartsOptions;
  chartInstance: Highcharts.Chart;
  showEmptyState = true;

  constructor(private zone: NgZone) {}

  ngAfterViewInit(): void {
    this.initializeOptions();
    // this.renderChart();  // was removed => prevent double loading
  }

  ngOnChanges(changes: SimpleChanges): void {
    /** 'changes.height' in condition is necessary for the charts to be rerendered always when passing the new height.
     * Now it works because additionalOptions is always passed, there might come a time when it isn't.
     * This creates a potential unobvious bug.
    */
    if (changes.additionalOptions || changes.height) {
      this.initializeOptions();
      setTimeout(() => {
        if (document.getElementById(this.chartId)) {
          this.reRenderChart();
        }
      }, 250);
    }
  }

  ngOnDestroy(): void {
    if (this.chartInstance) {
      this.chartInstance.destroy();
    }
  }

  initializeOptions(): void {
    const defaultOptions: HighchartsOptions = {
      chart: {
        type: 'pie',
        style: {
          fontFamily: 'Inter',
        },
      },
      title: {
        text: undefined,
        style: CHART_STYLES.title,
        align: 'left',
      },
      exporting: CHART_STYLES.hiddenExportingButton,
      tooltip: CHART_STYLES.tooltip,
      credits: CHART_STYLES.creditsHidden,
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: 'pointer',
          dataLabels: {
            enabled: true,
            format: '<b>{point.name}:</b><br>{point.y:.1f}',
            connectorColor: CHART_COLORS.black6,
            style: { color: CHART_COLORS.black12, fontSize: CHART_FONTS.font4 },
          },
          size: '70%',
          innerSize: '60%', // make chart empty inside
          states: CHART_STYLES.pieChartStates,
        },
      },
      series: [{
        name: '',
        data: [],
      }],
      colors: CHART_COLORS.default,
      lang: CHART_STYLES.lang,
    };

    this.options = { ...defaultOptions, ...this.additionalOptions };
    this.checkEmptyState();
  }

  private reRenderChart(): void {
    this.zone.runOutsideAngular(() => {
      if (document.getElementById(this.chartId)) {
        if (this.chartInstance) {
          this.chartInstance.destroy();
        }
        this.chartInstance = Highcharts.chart(this.chartId, this.options);
      }
    });
  }

  private renderChart(): void {
    this.zone.runOutsideAngular(() => {
      if (document.getElementById(this.chartId)) {
        this.chartInstance = Highcharts.chart(this.chartId, this.options);
      }
    });
  }

  /**
   * Show empty state when all series have no data
   */
  private checkEmptyState(): void {
    this.showEmptyState = this.options.series.every((series) => !series.data || series.data.length === 0);
  }
}
